/**
 * ActivationNudge - Contextual tooltips guiding new users to key actions
 * Shows once per nudge type, dismisses on action or manual close
 * @param {string} type - 'spot' | 'favorite'
 * @param {function} onDismiss - called when nudge is dismissed
 * @atomic @idempotent
 */
import { memo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, MapPinIcon, HeartIcon } from '@heroicons/react/24/outline'
import { createLogger } from '../../utils/logger'
import { trackEvent } from '../../utils/analytics'

const logger = createLogger('ActivationNudge')

const NUDGE_STORAGE_PREFIX = 'nudge_v1_'
const NUDGE_EXPIRY_DAYS = 7

const NUDGE_CONFIG = {
  spot: {
    Icon: MapPinIcon,
    translationKey: 'onboarding.welcome.nudge.spot',
    event: 'nudge_spot',
  },
  favorite: {
    Icon: HeartIcon,
    translationKey: 'onboarding.welcome.nudge.favorite',
    event: 'nudge_favorite',
  },
}

export function hasNudgeBeenShown(type) {
  try {
    const key = NUDGE_STORAGE_PREFIX + type
    const stored = localStorage.getItem(key)
    if (!stored) return false
    const { shownAt } = JSON.parse(stored)
    const expiryMs = NUDGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000
    return Date.now() - shownAt < expiryMs
  } catch {
    return false
  }
}

export function markNudgeShown(type) {
  try {
    localStorage.setItem(NUDGE_STORAGE_PREFIX + type, JSON.stringify({ shownAt: Date.now() }))
  } catch (e) {
    logger.warn('Could not persist nudge state', e)
  }
}

const ActivationNudge = memo(function ActivationNudge({ type, onDismiss, onAction }) {
  const { t } = useTranslation()
  const config = NUDGE_CONFIG[type]

  useEffect(() => {
    if (!config) return
    markNudgeShown(type)
    trackEvent('Onboarding', 'nudge_shown', config.event)
  }, [type, config])

  const handleDismiss = useCallback(() => {
    trackEvent('Onboarding', 'nudge_dismissed', config?.event)
    onDismiss?.()
  }, [config, onDismiss])

  const handleAction = useCallback(() => {
    trackEvent('Onboarding', 'nudge_actioned', config?.event)
    onAction?.()
    onDismiss?.()
  }, [config, onAction, onDismiss])

  if (!config) return null

  return (
    <div
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 max-w-xs w-full animate-slide-up"
      role="status"
      aria-live="polite"
    >
      <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-lg ring-1 ring-primary/20 overflow-hidden">
        {/* Brand accent strip */}
        <div className="h-0.5 bg-primary" />
        <div className="flex items-start gap-3 px-4 py-3.5">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <config.Icon className="h-5 w-5 text-primary" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800">
              {t(`${config.translationKey}.title`)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {t(`${config.translationKey}.description`)}
            </p>
            <button
              type="button"
              onClick={handleAction}
              onTouchEnd={(e) => { e.preventDefault(); handleAction() }}
              className="mt-2 inline-flex items-center text-xs font-semibold text-primary hover:underline focus:outline-none"
            >
              {t(`${config.translationKey}.cta`)}
            </button>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            onTouchEnd={(e) => { e.preventDefault(); handleDismiss() }}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors mt-0.5"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="h-4 w-4 pointer-events-none" />
          </button>
        </div>
      </div>
    </div>
  )
})

export default ActivationNudge
